import {
    Box,
    Button,
    ButtonGroup,
    IconButton,
    InputBase,
    Stack,
    Tooltip,
    Typography,
    buttonGroupClasses,
} from '@mui/material';
import { useState } from 'react';
import {
    BackupRounded,
    CloudDoneRounded,
    CloudSyncRounded,
    KeyboardArrowDownRounded,
    VisibilityRounded,
} from '@mui/icons-material';
import { useSlate } from 'slate-react';
import { HistoryEditor } from 'slate-history';
import Preview from './Preview';
import { useTranslation } from 'react-i18next';
import { ArrowBackIosIcon, RedoIcon, UndoIcon } from '@Components/dir-icons';
import TemplateDialog from '@Template/TemplateDialog';
import PrimaryButton from '@Components/PrimaryButton';
import useEditorProps from '@hooks/useEditorProps';
import useEmailEditor from '@hooks/useEmailEditor';

const savingMap = {
    saving: {
        text: 'Saving...',
        Icon: CloudSyncRounded,
    },
    saved: {
        text: 'Changes saved',
        Icon: CloudDoneRounded,
    },
    pending: {
        text: 'Unsaved changes.',
        Icon: BackupRounded,
    },
};

function Header() {
    const { t } = useTranslation();
    const editor = useSlate() as HistoryEditor;
    const { title, setTitle, navigateBack, selectTamplateOpen } =
        useEditorProps();
    const { saveStatus } = useEmailEditor();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [templateOpened, setTemplateOpened] = useState(selectTamplateOpen);

    const saveText = t(savingMap[saveStatus].text);
    const SaveIcon = savingMap[saveStatus].Icon;

    const canUndo = editor.history.undos.length;
    const canRedo = editor.history.redos.length;

    return (
        <Box
            display="flex"
            alignItems="center"
            sx={{
                borderBottom: 1,
                borderColor: 'divider',
                padding: 2,
                gap: 2,
                pr: 6,
            }}
        >
            <Box minWidth={300} display="flex" alignItems="center">
                <IconButton onClick={navigateBack}>
                    <ArrowBackIosIcon />
                </IconButton>
                <Typography variant="h6">
                    <InputBase
                        sx={{
                            all: 'inherit',
                            borderRadius: 1,
                            outlineStyle: 'solid',
                            '& .MuiInputBase-input': {
                                minWidth: 160,
                                width: title.length + 'ch',
                                p: 0,
                            },
                            ':hover': {
                                outlineColor: 'divider',
                                outlineWidth: 1,
                            },
                            ':focus-within': {
                                outlineColor: theme =>
                                    theme.palette.primary.main,
                                outlineWidth: 2,
                            },
                        }}
                        defaultValue={title}
                        onBlur={e => setTitle(e.target.value)}
                    />
                </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" mx={2}>
                <SaveIcon fontSize="inherit" color="disabled" />
                <Typography variant="subtitle2" color="text.disabled">
                    {saveText}
                </Typography>
            </Stack>
            <Box sx={{ flexGrow: 1, textAlign: 'center' }}></Box>
            <Box>
                <Tooltip title={t('Undo')}>
                    <span>
                        <IconButton
                            disabled={!canUndo}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => editor.undo()}
                        >
                            <UndoIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('Redo')} placement="top">
                    <span>
                        <IconButton
                            disabled={!canRedo}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => editor.redo()}
                        >
                            <RedoIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <Stack direction="row" spacing={2} marginX={2}>
                <Button
                    onClick={() => setPreviewOpen(true)}
                    variant="outlined"
                    color="primary"
                    startIcon={<VisibilityRounded />}
                >
                    {t('Preview')}
                </Button>
                <PrimaryButton showTemplates={() => setTemplateOpened(true)} />
            </Stack>
            <Preview open={previewOpen} onClose={() => setPreviewOpen(false)} />
            <TemplateDialog
                open={templateOpened}
                onClose={() => setTemplateOpened(false)}
            />
        </Box>
    );
}

export default Header;
